import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Briefcase, Save, X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCreateTeamMemberMutation } from '../../store/teamMemberApi';
import { useGetVendorMeQuery } from '../../store/vendorApi';
import { toast } from '@clareefai/ui';
import EmailPreviewCard from '../../components/shared/EmailPreviewCard';
import { EmailTemplateType } from '../../store/emailTemplateApi';

const AddTeamMember: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [createTeamMember, { isLoading: isCreating }] = useCreateTeamMemberMutation();
  const { data: vendor } = useGetVendorMeQuery();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const roles = [
    { id: 'Marketing Manager', labelKey: 'teams.roles.marketingManager', color: 'amber' },
    { id: 'Manager', labelKey: 'teams.roles.manager', color: 'blue' },
    { id: 'Account Manager', labelKey: 'teams.roles.accountManager', color: 'indigo' },
    { id: 'Customer Success', labelKey: 'teams.roles.customerSuccess', color: 'green' },
    { id: 'Account Executive', labelKey: 'teams.roles.accountExecutive', color: 'purple' }
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('teams.add.firstNameRequired');
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t('teams.add.lastNameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('teams.add.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('teams.add.emailInvalid');
    }

    if (!formData.role) {
      newErrors.role = t('teams.add.roleRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        await createTeamMember({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: formData.role as 'Account Executive' | 'Customer Success' | 'Account Manager' | 'Manager' | 'Marketing Manager',
        }).unwrap();
        
        toast.success(t('teams.add.createSuccess'));
        navigate('/teams-sales');
      } catch (err: any) {
        console.error('Failed to create team member:', err);
        toast.error(err?.data?.message || t('teams.add.createError'));
      }
    }
  };

  const handleCancel = () => {
    navigate('/teams-sales');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="bg-gradient-to-r from-[#4339A0] via-[#5a4dba] to-[#3d2f8f] text-white">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button
            onClick={handleCancel}
            className="mb-6 flex items-center space-x-2 text-[#b8b0f0] hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('teams.add.backToTeams')}</span>
          </button>

          <div>
            <h1 className="text-4xl font-bold mb-2">{t('teams.add.heading')}</h1>
            <p className="text-xl text-[#b8b0f0]">{t('teams.add.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="bg-white rounded-xl shadow-sm">
          <form onSubmit={handleSubmit}>
            <div className="p-8 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4" />
                    <span>{t('teams.add.firstName')}</span>
                  </div>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder={t('teams.add.firstNamePlaceholder')}
                  disabled={isCreating}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent transition-colors ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  } ${isCreating ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4" />
                    <span>{t('teams.add.lastName')}</span>
                  </div>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder={t('teams.add.lastNamePlaceholder')}
                  disabled={isCreating}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent transition-colors ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  } ${isCreating ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4" />
                    <span>{t('teams.add.email')}</span>
                  </div>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('teams.add.emailPlaceholder')}
                  disabled={isCreating}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent transition-colors ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } ${isCreating ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Briefcase className="w-4 h-4" />
                    <span>{t('teams.add.role')}</span>
                  </div>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  disabled={isCreating}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent transition-colors ${
                    errors.role ? 'border-red-500' : 'border-gray-300'
                  } ${isCreating ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">{t('teams.add.selectRole')}</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {t(role.labelKey)}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">{errors.role}</p>
                )}
                <p className="mt-2 text-sm text-gray-500">
                  {t('teams.add.roleHelp')}
                </p>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-4">
                  {t('teams.add.requiredFields')}
                </p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isCreating}
                    className={`flex items-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg transition-colors ${
                      isCreating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                    }`}
                  >
                    <X className="w-4 h-4" />
                    <span>{t('teams.add.cancel')}</span>
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating}
                    className={`flex items-center space-x-2 px-6 py-3 bg-[#4339A0] text-white rounded-lg transition-colors ${
                      isCreating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#362d85]'
                    }`}
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>{t('teams.add.creating')}</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>{t('teams.add.addMember')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Email Preview */}
        <EmailPreviewCard
          templateType={EmailTemplateType.TEAM_MEMBER_INVITE}
          sampleData={{
            firstName: formData.firstName || 'there',
            inviterName: vendor ? [vendor.firstName, vendor.lastName].filter(Boolean).join(' ') : '[Your Name]',
            companyName: vendor?.companyName || '[Company Name]',
            role: formData.role || '[Role]',
          }}
        />
      </div>
    </div>
  );
};

export default AddTeamMember;
